import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ecosystemPodcast from '../assets/EcosystemData/Podcast/Winning the McKinsey SOLVE Ecosystem Game.wav';
import ecosystemHeroImage from '../assets/images/Image 1_Ecosystem.jpg';
import SEO from '../components/SEO';

const styles = `
  .feature-card {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    transform-style: preserve-3d;
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%);
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.5s forwards;
    border: 1px solid rgba(59, 130, 246, 0.1);
  }

  .feature-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 24px -10px rgba(66, 153, 225, 0.3);
    border-color: rgba(59, 130, 246, 0.2);
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(239, 246, 255, 0.9) 100%);
  }

  .feature-icon {
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
    border-radius: 12px;
    margin-bottom: 1rem;
  }

  .feature-card:hover .feature-icon {
    transform: scale(1.1) rotate(-5deg);
    background: linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%);
  }

  .section-title {
    background: linear-gradient(135deg, #000 0%, #333 25%, #000 50%, #666 75%, #000 100%);
    -webkit-background-clip: text;
    color: transparent;
    position: relative;
    display: inline-block;
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.1);
  }

  .section-title::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.3) 25%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.3) 75%, transparent 100%);
    border-radius: 2px;
  }

  .microbe-card {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%);
    border: 1px solid rgba(59, 130, 246, 0.1);
    position: relative;
    overflow: hidden;
  }

  .microbe-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(to right, #60A5FA, #93C5FD);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .microbe-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 20px 30px -15px rgba(66, 153, 225, 0.25);
    border-color: rgba(59, 130, 246, 0.2);
  }

  .microbe-card:hover::before {
    opacity: 1;
  }

  .tip-box {
    background: linear-gradient(165deg, #F0F9FF 0%, #E0F2FE 100%);
    border-left: 4px solid #60A5FA;
    border-radius: 8px;
  }

  .scenario-card {
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%);
    border: 1px solid rgba(59, 130, 246, 0.1);
    transition: all 0.3s ease;
  }

  .scenario-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px -10px rgba(66, 153, 225, 0.3);
  }

  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .hero-content {
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.6s forwards;
  }

  .hero-image {
    opacity: 0;
    transform: translateX(20px);
    animation: fadeInRight 0.8s 0.2s forwards;
  }

  @keyframes fadeInRight {
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes pulse-glow {
    0% {
      box-shadow: 0 0 5px rgba(59, 130, 246, 0.5),
                  0 0 10px rgba(59, 130, 246, 0.3),
                  0 0 15px rgba(59, 130, 246, 0.2);
    }
    50% {
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.8),
                  0 0 30px rgba(59, 130, 246, 0.5),
                  0 0 40px rgba(59, 130, 246, 0.3);
    }
    100% {
      box-shadow: 0 0 5px rgba(59, 130, 246, 0.5),
                  0 0 10px rgba(59, 130, 246, 0.3),
                  0 0 15px rgba(59, 130, 246, 0.2);
    }
  }

  @keyframes text-glow {
    0% {
      text-shadow: 0 0 5px rgba(255, 255, 255, 0.5),
                   0 0 10px rgba(255, 255, 255, 0.3);
    }
    50% {
      text-shadow: 0 0 10px rgba(255, 255, 255, 0.8),
                   0 0 20px rgba(255, 255, 255, 0.5),
                   0 0 30px rgba(255, 255, 255, 0.3);
    }
    100% {
      text-shadow: 0 0 5px rgba(255, 255, 255, 0.5),
                   0 0 10px rgba(255, 255, 255, 0.3);
    }
  }

  .coming-soon-banner {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  .coming-soon-text {
    animation: text-glow 2s ease-in-out infinite;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default function EcosystemGuide() {
  const navigate = useNavigate();
  const [showScoringDetails, setShowScoringDetails] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [activeVideoTab, setActiveVideoTab] = useState('tutorial1');

  const scenarios = [
    {
      type: 'Perfect Harmony',
      score: '100%',
      description: 'All species survive and thrive',
      icon: '🌊'
    },
    {
      type: 'Balanced Chain',
      score: '100%',
      description: 'Sustainable food chain achieved',
      icon: '🔄'
    },
    {
      type: 'Partial Success',
      score: '80%',
      description: 'Most species survive',
      icon: '⚖️'
    },
    {
      type: 'Failed Chain',
      score: '0%',
      description: 'Food chain breaks down',
      icon: '❌'
    }
  ];

  const gameSteps = [
    {
      step: 1,
      title: 'Identify Ecosystem Bundles',
      description: 'Group species by environmental conditions',
      icon: '🔍',
      tip: 'Look for three distinct combinations of depth/temperature ranges'
    },
    {
      step: 2,
      title: 'Select Bundle',
      description: 'Choose bundle with highest producer calories',
      icon: '📊',
      tip: 'Sum up producer calories for each bundle to find the best one'
    },
    {
      step: 3,
      title: 'Build Food Chain',
      description: 'Start with producers, then add animals',
      icon: '🔗',
      tip: 'Always include all three producers from your chosen bundle'
    },
    {
      step: 4,
      title: 'Verify Sustainability',
      description: 'Check if all species can survive',
      icon: '✅',
      tip: 'Track calorie changes after each species eats'
    },
    {
      step: 5,
      title: 'Choose Location',
      description: 'Select suitable environment for all species',
      icon: '📍',
      tip: 'Match depth and temperature requirements of your chosen species'
    }
  ];

  const videoTabs = [
    {
      id: 'tutorial1',
      title: 'Ecosystem Tutorial 1',
      videoId: 'tPkEL5aL0C0',
      startTime: 0
    },
    {
      id: 'tutorial2',
      title: 'Ecosystem Tutorial 2',
      videoId: 'qdBJrKlHEh0',
      startTime: 487
    }
  ];

  return (
    <div className="bg-white text-gray-900 font-sans">
      <SEO
        title="McKinsey Ecosystem Guide | Cognidoor"
        description="Prepare for the McKinsey Solve ecosystem simulation. Strategy, creature selection, calorie logic, and worked examples."
        url="/ecosystemguide"
        noIndex
      />
      {/* Hero Section */}
      <section className="relative z-30 bg-gradient-to-br from-blue-100 via-blue-50 to-white py-10 md:pb-8 min-h-[320px] md:min-h-[500px] flex items-center overflow-visible">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 via-gray-800/70 to-gray-700/60">
          <div className="absolute inset-0" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }} />
        </div>
        <div className="container pl-0 md:pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 py-0 md:py-16 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-sm md:max-w-2xl mx-auto md:mx-0 hero-content text-center md:text-left"
          >
            <div className="mb-4 md:mb-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-block bg-gradient-to-r from-blue-600 to-blue-400 text-white px-4 md:px-6 py-1.5 md:py-2 rounded-full shadow-lg mb-2 md:mb-4 coming-soon-banner text-sm md:text-lg"
              >
                <span className="font-bold tracking-wide coming-soon-text">ESSENTIAL GUIDE</span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-lg md:text-2xl font-bold text-white/90 mt-2"
              >
                Learn more about the Ecosystem Game 
              </motion.div>
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-6xl font-bold mb-3 md:mb-6 leading-tight text-white">Master the Ecosystem Game</h1>
            <p className="text-base md:text-xl lg:text-2xl text-white/90 mb-6 md:mb-10 leading-relaxed">Learn how to build sustainable food chains and restore ecosystems in McKinsey's Problem Solving Game. Your success depends on strategic species selection and understanding complex food web dynamics.</p>
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 mt-4 md:mt-8 w-full items-center md:items-start">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/ecosystem-solver')}
                className="group relative px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-base md:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300 min-h-[44px] w-full md:w-auto"
              >
                Give our solver a shot
                <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowVideoModal(true)}
                className="group relative px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-700 to-gray-600 text-white text-base md:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300 min-h-[44px] w-full md:w-auto"
              >
                See Ecosystem Guide Videos
                <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              </motion.button>
            </div>
          </motion.div>
        </div>
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="hidden md:block absolute top-[80px] right-10 md:right-20 w-[400px] aspect-[2/3] z-10 hero-image"
        >
          <div className="h-full rounded-xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
            <img 
              src={ecosystemHeroImage}
              alt="Ecosystem Game Hero" 
              className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
            />
          </div>
        </motion.div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-8 bg-gradient-to-b from-blue-50/50 to-white relative">
        <div className="max-w-[90rem] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-blue-100"
            >
              <div className="text-2xl bg-blue-50 p-2 rounded-lg">⏱️</div>
              <div>
                <h3 className="text-base font-semibold text-gray-800">Time Limit</h3>
                <p className="text-sm text-gray-600">35 minutes total game time</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-blue-100"
            >
              <div className="text-2xl bg-blue-50 p-2 rounded-lg">🎯</div>
              <div>
                <h3 className="text-base font-semibold text-gray-800">Game Structure</h3>
                <p className="text-sm text-gray-600">39 species, 8 to select</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-blue-100"
            >
              <div className="text-2xl bg-blue-50 p-2 rounded-lg">💡</div>
              <div>
                <h3 className="text-base font-semibold text-gray-800">Success Metric</h3>
                <p className="text-sm text-gray-600">Sustainable food chain</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="lg:col-span-1 lg:ml-[-1rem] lg:mr-[-8rem] p-4 bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-sm border border-blue-100 hover:shadow-md transition-shadow duration-300"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="text-2xl bg-blue-50 p-2 rounded-lg">🎧</div>
                <h3 className="text-base font-semibold text-gray-800">Listen & Learn</h3>
              </div>
              <p className="text-xs text-gray-600 mb-3">
                Expert walkthrough of the Ecosystem Game
              </p>
              <div className="bg-white rounded-lg p-3 shadow-inner">
                <audio 
                  controls 
                  className="w-full h-10"
                  controlsList="nodownload"
                  style={{ minWidth: '100%' }}
                >
                  <source src={ecosystemPodcast} type="audio/wav" />
                  Your browser does not support the audio element.
                </audio>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Game Introduction Section */}
      <section className="py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-6 relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
        </div>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold section-title mb-6">Welcome to the Ecosystem Game</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              A sophisticated simulation where you'll analyze and select species to build sustainable food chains. Your strategic decisions directly impact your success in restoring the ecosystem.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="feature-card rounded-2xl p-8 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-bl-full transform translate-x-16 -translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-100 rounded-tr-full transform -translate-x-16 translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="feature-icon text-3xl mb-6 bg-gradient-to-br from-blue-100 to-blue-50">🌊</div>
                <h3 className="text-2xl font-semibold mb-4 text-gray-800">Core Game Mechanics</h3>
                <p className="text-gray-600 leading-relaxed">
                  The game presents you with 39 species (9 producers and 30 animals) to choose from. Your goal is to select 8 species that can form a sustainable food chain, where each species survives and thrives in its environment.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="feature-card rounded-2xl p-8 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-bl-full transform translate-x-16 -translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-100 rounded-tr-full transform -translate-x-16 translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="feature-icon text-3xl mb-6 bg-gradient-to-br from-blue-100 to-blue-50">🎯</div>
                <h3 className="text-2xl font-semibold mb-4 text-gray-800">Success Criteria</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Your success depends on three critical factors:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>All 8 selected species must survive</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>Food chain must be sustainable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>Location must support all species</span>
                  </li>
                </ul>
                <div className="tip-box p-4 mt-4 bg-gradient-to-r from-blue-50 to-blue-100/50 border-l-4 border-blue-400">
                  <p className="text-blue-700 flex items-start gap-2">
                    <span className="text-xl mt-0.5">💡</span>
                    <span>Always build your food chain first, then choose the location!</span>
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="feature-card rounded-2xl p-8 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-bl-full transform translate-x-16 -translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-100 rounded-tr-full transform -translate-x-16 translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="feature-icon text-3xl mb-6 bg-gradient-to-br from-blue-100 to-blue-50">⏱️</div>
                <h3 className="text-2xl font-semibold mb-4 text-gray-800">Time Management</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  With 35 minutes total, efficient decision-making is crucial. Here's how to allocate your time:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>~5 minutes to analyze species</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>~25 minutes to build food chain</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>~5 minutes to verify and submit</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="feature-card rounded-2xl p-8 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-bl-full transform translate-x-16 -translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-100 rounded-tr-full transform -translate-x-16 translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="feature-icon text-3xl mb-6 bg-gradient-to-br from-blue-100 to-blue-50">📋</div>
                <h3 className="text-2xl font-semibold mb-4 text-gray-800">Species Categories</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  The game features two types of species:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>9 Producers (self-sustaining)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>30 Animals (need food)</span>
                  </li>
                </ul>
                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50/50 to-transparent rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-700">
                    Each species has specific environmental requirements and food chain relationships.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Game Flow Section */}
      <section className="py-20 bg-gradient-to-b from-white via-blue-50/30 to-white px-6 relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold section-title">Game Flow: 5 Steps to Success</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {gameSteps.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="feature-card rounded-2xl p-8"
              >
                <div className="feature-icon text-3xl mb-6">{step.icon}</div>
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Step {step.step}: {step.title}</h3>
                <p className="text-gray-600 mb-4">{step.description}</p>
                <div className="tip-box p-4 mt-4">
                  <p className="text-sm text-blue-700">💡 {step.tip}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Scoring & Scenarios Section */}
      <section className="py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-6 relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold section-title">Scoring & Scenarios</h2>
          </div>
          
          {/* Scoring Table */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
            <h3 className="text-2xl font-semibold mb-6 text-gray-800">Scoring Criteria</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-blue-50">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Criteria</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Goal</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-600">All species survive?</td>
                    <td className="px-6 py-4 text-sm text-green-600">✅ Yes</td>
                    <td className="px-6 py-4 text-sm text-red-600">Fail if any die</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-600">Food chain sustainable?</td>
                    <td className="px-6 py-4 text-sm text-green-600">✅ Yes</td>
                    <td className="px-6 py-4 text-sm text-red-600">Fail if breaks</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-600">Location suitable?</td>
                    <td className="px-6 py-4 text-sm text-green-600">✅ Yes</td>
                    <td className="px-6 py-4 text-sm text-red-600">Fail if not</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-600">Correct species count?</td>
                    <td className="px-6 py-4 text-sm text-green-600">✅ 8 species</td>
                    <td className="px-6 py-4 text-sm text-red-600">Fail if not 8</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-600">Bundle selection?</td>
                    <td className="px-6 py-4 text-sm text-green-600">✅ One bundle</td>
                    <td className="px-6 py-4 text-sm text-red-600">Fail if mixed</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Scenarios Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {scenarios.map((scenario, i) => (
              <motion.div
                key={scenario.type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="scenario-card rounded-xl p-6 text-center"
              >
                <div className="text-4xl mb-4">{scenario.icon}</div>
                <h3 className="text-xl font-semibold mb-2 text-gray-800">{scenario.type}</h3>
                <div className="text-2xl font-bold text-blue-600 mb-2">{scenario.score}</div>
                <p className="text-gray-600 text-sm">{scenario.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategy Tips Section */}
      <section className="py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-6 relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold section-title">Strategy Tips</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="feature-card rounded-2xl p-8"
            >
              <div className="feature-icon text-3xl mb-6">⏱️</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Time Management & Workflow</h3>
              <ul className="space-y-3 text-gray-600">
                <li><strong>35 minutes total game time</strong> — allocate your time wisely: 5 min for bundle analysis, 25 min for food chain building, 5 min for verification and submission.</li>
                <li><strong>Don't get stuck on impossible bundles:</strong> If you can't find a sustainable chain after 5–7 minutes, switch to another bundle. Not all bundles are solvable!</li>
                <li><strong>Work in layers:</strong> First, shortlist bundles by producer calories. Then, shortlist food chains by sustainability. Only then check location compatibility.</li>
                <li><strong>Use the solver:</strong> If available, use tools to quickly test food chain sustainability and avoid manual calculation errors.</li>
                <li><strong>Keep notes:</strong> Jot down calorie flows and bundle characteristics as you go. This helps you avoid repeating work if you need to switch bundles.</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="feature-card rounded-2xl p-8"
            >
              <div className="feature-icon text-3xl mb-6">🧮</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Advanced Food Chain & Bundle Strategy</h3>
              <div className="tip-box p-4 mb-4">
                <p className="text-blue-700">
                  <strong>Bundle Selection:</strong> Always select a bundle with the highest total producer calories. This maximizes the energy available for the rest of the chain.
                </p>
              </div>
              <ul className="space-y-3 text-gray-600">
                <li><strong>Always include all three producers from your chosen bundle.</strong> They are the foundation of your food chain and required for sustainability.</li>
                <li><strong>Build upward:</strong> Add animals one by one, ensuring each can eat from the previous level and that calories provided never drops to zero.</li>
                <li><strong>Check for calorie bottlenecks:</strong> If a species provides fewer calories than the next one needs, the chain will break. Track calories after each feeding step.</li>
                <li><strong>Don't mix bundles:</strong> All 8 species must come from the same environmental bundle. Mixing will result in an automatic fail.</li>
                <li><strong>Location comes last:</strong> Only after you have a sustainable chain should you check if a location supports all 8 species' depth and temperature needs.</li>
                <li><strong>Be flexible:</strong> If you hit a dead end, don't hesitate to backtrack and try a different animal combination or even a new bundle.</li>
                <li><strong>Use process of elimination:</strong> If a species can't be fed or doesn't fit the chain, eliminate it early to save time.</li>
                <li><strong>Common pitfalls:</strong> Forgetting to include all three producers, mixing bundles, or not checking calorie flows at every step.</li>
              </ul>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="feature-card rounded-2xl p-8"
            >
              <div className="feature-icon text-3xl mb-6">🔗</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Food Web Logic & Practical Tips</h3>
              <ul className="space-y-3 text-gray-600">
                <li><strong>Producers are self-sustaining:</strong> They don't need to eat, but must be present for animals to survive.</li>
                <li><strong>Each animal must have a valid food source:</strong> Check the food web chart to ensure every animal can eat from a lower level.</li>
                <li><strong>Don't double-count calories:</strong> Each feeding step reduces the calories available for the next consumer. Update calorie counts after every step.</li>
                <li><strong>Watch for dead ends:</strong> If an animal can't eat or be eaten, it will die and break the chain.</li>
                <li><strong>Use the Excel Solver (if allowed):</strong> It can quickly check if a sustainable chain is possible for your bundle.</li>
                <li><strong>Practice with sample data:</strong> The more you practice, the faster you'll spot sustainable chains and avoid common traps.</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="feature-card rounded-2xl p-8"
            >
              <div className="feature-icon text-3xl mb-6">⚠️</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Common Mistakes & How to Avoid Them</h3>
              <ul className="space-y-3 text-gray-600">
                <li><strong>Forgetting to include all three producers</strong> — always start your chain with them.</li>
                <li><strong>Mixing species from different bundles</strong> — stick to one bundle for all 8 species.</li>
                <li><strong>Not checking calorie flows at every step</strong> — a single break means failure.</li>
                <li><strong>Ignoring environmental requirements</strong> — verify location compatibility only after building your chain.</li>
                <li><strong>Spending too long on unsolvable chains</strong> — if stuck, switch bundles quickly.</li>
                <li><strong>Not using available tools</strong> — solvers and calculators can save you time and prevent errors.</li>
                <li><strong>Not practicing before the real game</strong> — practice builds speed and confidence.</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Modal for Scoring Details */}
      <AnimatePresence>
        {showScoringDetails && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4"
          >
            <motion.div 
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="bg-white rounded-2xl p-8 max-w-2xl w-full shadow-xl"
            >
              <h3 className="text-2xl font-bold mb-6 text-gray-800">Scoring Guide</h3>
              <div className="space-y-4">
                <p className="text-gray-600">
                  Your score depends on how well your chosen species form a sustainable food chain:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>100% = perfect sustainable chain</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">×</span>
                    <span>0% if any species dies or chain breaks</span>
                  </li>
                </ul>
                <div className="tip-box p-4 mt-4">
                  <p className="text-blue-700">
                    💡 If you can't find a sustainable chain in your chosen bundle, quickly switch to another bundle.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex justify-end">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowScoringDetails(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4"
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div 
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="bg-white rounded-2xl p-4 max-w-4xl w-full shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Video Tabs */}
              <div className="flex space-x-2 mb-4 border-b border-gray-200">
                {videoTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveVideoTab(tab.id)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                      activeVideoTab === tab.id
                        ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    {tab.title}
                  </button>
                ))}
              </div>

              {/* Video Container */}
              <div className="relative pb-[56.25%] h-0 rounded-lg overflow-hidden bg-gray-100">
                {videoTabs.map((tab) => (
                  <div
                    key={tab.id}
                    className={`absolute top-0 left-0 w-full h-full transition-opacity duration-300 ${
                      activeVideoTab === tab.id ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                  >
                    <iframe
                      className="absolute top-0 left-0 w-full h-full"
                      src={`https://www.youtube.com/embed/${tab.videoId}?start=${tab.startTime}`}
                      title={tab.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex justify-end">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowVideoModal(false)}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Call-to-Action Button */}
      <div className="flex justify-center py-16">
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/ecosystem-solver')}
          className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
        >
          Give our solver a shot
          <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
        </motion.button>
      </div>
    </div>
  );
} 